'use client';
import React, { useState } from 'react';
import { Mail, MapPin, MessageSquare, Send, CheckCircle, Clock, HelpCircle, ExternalLink, Loader2 } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { SITE_CONTACT } from '@/lib/siteContact';

export default function ContactContent() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', website: '' });
  const { t, language } = useLanguage();
  const cp = t?.contactPage;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: form.name,
          email: form.email,
          topic: form.subject,
          message: form.message,
          locale: language,
          website: form.website,
        }),
      });

      let data: { success?: boolean; error?: string } = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (!res.ok || !data.success) {
        setError(
          data.error ||
            cp?.submitError ||
            'We could not send your message. Please try again or email support.',
        );
        return;
      }

      setSubmitted(true);
    } catch {
      setError(
        cp?.submitError ||
          'We could not send your message. Please try again or email support.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-background pt-24 pb-20">
      {/* Header */}
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-14">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <MessageSquare size={13} className="text-primary" />
            <span className="text-xs font-600 text-primary">{cp?.badge ?? 'Contact Us'}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-800 text-foreground mb-5 leading-tight">
            {cp?.headline ?? "We'd Love to"}{' '}
            <span className="gradient-text">{cp?.headlineHighlight ?? 'Hear From You'}</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {cp?.intro ?? 'Have a question, a bug to report, a partnership idea, or just want to say hello? We read every message and respond to every inquiry. The AIMaker Studio team is here to help.'}
          </p>
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-5 gap-10">
          {/* Contact Info */}
          <div className="md:col-span-2 flex flex-col gap-5">
            {/* Email */}
            <div className="glass-card rounded-xl p-6 border border-border">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Mail size={18} className="text-primary" />
              </div>
              <h3 className="text-sm font-700 text-foreground mb-1">{cp?.supportEmailTitle ?? 'Support Email'}</h3>
              <p className="text-sm text-muted-foreground font-500">
                <a href={`mailto:${SITE_CONTACT.support}`} className="hover:text-foreground transition-colors">
                  {SITE_CONTACT.support}
                </a>
              </p>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                {cp?.supportEmailDesc ?? 'For account issues, billing questions, technical support, and general inquiries. We aim to respond within 1–2 business days.'}
              </p>
            </div>

            {/* Business Hours */}
            <div className="glass-card rounded-xl p-6 border border-border">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Clock size={18} className="text-primary" />
              </div>
              <h3 className="text-sm font-700 text-foreground mb-3">{cp?.businessHoursTitle ?? 'Business Hours'}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{cp?.monFri ?? 'Monday – Friday'}</span>
                  <span className="text-foreground font-500">{cp?.monFriHours ?? '9:00 AM – 6:00 PM ET'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{cp?.saturday ?? 'Saturday'}</span>
                  <span className="text-foreground font-500">{cp?.saturdayHours ?? '10:00 AM – 3:00 PM ET'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{cp?.sunday ?? 'Sunday'}</span>
                  <span className="text-muted-foreground">{cp?.sundayClosed ?? 'Closed'}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border/50">
                {cp?.outsideHoursNote ?? 'Messages received outside business hours are answered the next business day.'}
              </p>
            </div>

            {/* Headquarters */}
            <div className="glass-card rounded-xl p-6 border border-border">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <MapPin size={18} className="text-primary" />
              </div>
              <h3 className="text-sm font-700 text-foreground mb-1">{cp?.headquartersTitle ?? 'Headquarters'}</h3>
              <p className="text-sm text-muted-foreground">{cp?.hqCompany ?? 'BeMel Productions LLC'}</p>
              <p className="text-sm text-muted-foreground">{cp?.hqLocation ?? 'Georgia, United States'}</p>
              <p className="text-xs text-muted-foreground mt-2">{cp?.hqNote ?? 'AIMaker Studio is a product of BeMel Productions LLC.'}</p>
            </div>

            {/* Quick Links */}
            <div className="glass-card rounded-xl p-6 border border-border">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <HelpCircle size={18} className="text-primary" />
              </div>
              <h3 className="text-sm font-700 text-foreground mb-3">{cp?.beforeYouWriteTitle ?? 'Before You Write'}</h3>
              <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{cp?.beforeYouWriteDesc ?? 'Many common questions are already answered in our support resources:'}</p>
              <div className="flex flex-col gap-2 text-sm">
                <a href="/help-center" className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
                  <ExternalLink size={12} /> {cp?.helpCenter ?? 'Help Center & FAQ'}
                </a>
                <a href="/learn-center" className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
                  <ExternalLink size={12} /> {cp?.learnCenter ?? 'Learn Center Guides'}
                </a>
                <a href="/privacy-policy" className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
                  <ExternalLink size={12} /> {cp?.privacyPolicy ?? 'Privacy Policy'}
                </a>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="md:col-span-3">
            <div className="glass-card rounded-2xl p-8 border border-border">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <CheckCircle size={40} className="text-primary mb-4" />
                  <h2 className="text-xl font-700 text-foreground mb-2">{cp?.successTitle ?? 'Message Sent!'}</h2>
                  <p className="text-muted-foreground text-sm max-w-sm leading-relaxed">
                    {cp?.successDesc ?? "Thanks for reaching out to AIMaker Studio. We've received your message and will get back to you at your email address within 1–2 business days."}
                  </p>
                  <p className="text-xs text-muted-foreground mt-3">
                    {cp?.successUrgent ?? 'For urgent issues, you can also email us directly at'}{' '}
                    <a href={`mailto:${SITE_CONTACT.support}`} className="text-foreground hover:underline">
                      {SITE_CONTACT.support}
                    </a>
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setError(null);
                      setForm({ name: '', email: '', subject: '', message: '', website: '' });
                    }}
                    className="mt-6 btn-secondary px-5 py-2 rounded-lg text-sm"
                  >
                    {cp?.sendAnother ?? 'Send Another Message'}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div>
                    <h2 className="text-lg font-700 text-foreground mb-1">{cp?.sendMessageTitle ?? 'Send a Message'}</h2>
                    <p className="text-sm text-muted-foreground">{cp?.sendMessageDesc ?? "Fill out the form below and we'll get back to you as soon as possible."}</p>
                  </div>
                  {/* Honeypot — hidden from real users */}
                  <div className="absolute -left-[9999px] top-auto h-0 w-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="contact-website">Website</label>
                    <input
                      id="contact-website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-600 text-muted-foreground mb-1.5">{cp?.fullName ?? 'Full Name'} <span className="text-primary">*</span></label>
                      <input
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        maxLength={120}
                        disabled={submitting}
                        placeholder={cp?.namePlaceholder ?? 'Your full name'}
                        className="w-full bg-muted border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors disabled:opacity-60"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-600 text-muted-foreground mb-1.5">{cp?.emailAddress ?? 'Email Address'} <span className="text-primary">*</span></label>
                      <input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        maxLength={254}
                        disabled={submitting}
                        placeholder={cp?.emailPlaceholder ?? 'you@example.com'}
                        className="w-full bg-muted border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors disabled:opacity-60"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-600 text-muted-foreground mb-1.5">{cp?.topic ?? 'Topic'} <span className="text-primary">*</span></label>
                    <select
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      required
                      disabled={submitting}
                      className="w-full bg-muted border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors disabled:opacity-60"
                    >
                      <option value="">{cp?.selectTopic ?? 'Select a topic'}</option>
                      <option value="support">{cp?.topicSupport ?? 'Technical Support'}</option>
                      <option value="billing">{cp?.topicBilling ?? 'Billing & Subscriptions'}</option>
                      <option value="credits">{cp?.topicCredits ?? 'Credits & Usage'}</option>
                      <option value="feedback">{cp?.topicFeedback ?? 'Product Feedback'}</option>
                      <option value="bug">{cp?.topicBug ?? 'Report a Bug'}</option>
                      <option value="partnership">{cp?.topicPartnership ?? 'Partnership Inquiry'}</option>
                      <option value="careers">{cp?.topicCareers ?? 'General Interest / Careers'}</option>
                      <option value="press">{cp?.topicPress ?? 'Press & Media'}</option>
                      <option value="other">{cp?.topicOther ?? 'Other'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-600 text-muted-foreground mb-1.5">{cp?.message ?? 'Message'} <span className="text-primary">*</span></label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      maxLength={5000}
                      disabled={submitting}
                      placeholder={cp?.messagePlaceholder ?? 'Tell us how we can help. The more detail you provide, the faster we can assist you.'}
                      className="w-full bg-muted border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors resize-none disabled:opacity-60"
                    />
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border border-border/50">
                    <HelpCircle size={14} className="text-muted-foreground shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {cp?.formHint ?? 'For billing issues, please include your account email. For bug reports, include the tool you were using and a description of what happened. This helps us resolve your issue faster.'}
                    </p>
                  </div>
                  {error && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-foreground" role="alert">
                      <p className="leading-relaxed">{error}</p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {cp?.submitErrorFallback ?? 'Or email us directly at'}{' '}
                        <a href={`mailto:${SITE_CONTACT.support}`} className="text-foreground underline underline-offset-2">
                          {SITE_CONTACT.support}
                        </a>
                      </p>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary px-6 py-2.5 rounded-xl text-sm flex items-center gap-2 self-start disabled:opacity-60 disabled:pointer-events-none"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        {cp?.sendingBtn ?? 'Sending…'}
                      </>
                    ) : (
                      <>
                        <Send size={14} /> {cp?.sendBtn ?? 'Send Message'}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
